import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";

import { AppContext } from "../../context/AppContext";
import { useAuth as useWalletAuth } from "../../context/wallet/WalletAuthContext";
import {
  PostCallWithErrorResponse,
  simpleGetCallWithErrorResponse,
} from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { ToastMsg } from "../../pages/TostMsg";

// Workflow encrypted localStorage key + secret are preserved verbatim from
// the legacy Sidebar. The redirect target is opened by an external consumer;
// changing either value would break that handshake.
const ENCRYPTED_LS_KEY = "encrypted";
const ENCRYPTED_SECRET = "your-secret-key";

const SOCKET_CLEAR_CHAT = new Set([
  "getPrivateChat",
  "getProjects",
  "getTeams",
]);

export const useSidebar = ({ setLoggedIn }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentRoute = location.pathname;

  const {
    setUserData,
    userData,
    setSocket,
    socket,
    openSessionLogoutModal,
    setOpenSessionLogoutModal,
    setActiveChat,
    setCurrentSChannel,
    chatCount,
    unreadProjectCount,
    pendingWorkflowTaskCount,
    handleNavigate,
    sidebar,
    setSidebar,
    Dark,
    unreadCount,
    setProjects,
    setCallPage,
    setCallLoadMore,
    setTeams,
    unreadTeamCount,
  } = useContext(AppContext);

  const { isAdmin: isWalletAdmin } = useWalletAuth();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const permissions = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("Permissions")) || {};
    } catch {
      return {};
    }
    // Read once per session — matches legacy behaviour where permissions are
    // synchronously parsed at component setup time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const can = useCallback(
    (key) => {
      if (!key) return false;
      if (Array.isArray(key)) return key.some((k) => Boolean(permissions[k]));
      return Boolean(permissions[key]);
    },
    [permissions]
  );

  const canAny = useCallback(
    (keys) => {
      if (!keys || keys.length === 0) return true;
      return keys.some((k) => Boolean(permissions[k]));
    },
    [permissions]
  );

  // Inverted top-level gate from legacy Sidebar.js:1018 —
  // when "Employee Wallet" === true, the regular menu is suppressed.
  const isEmployeeWalletOnly = permissions["Employee Wallet"] === true;

  useEffect(() => {
    setSidebar(!isMobile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const toggleCollapse = useCallback(
    () => setSidebar((v) => !v),
    [setSidebar]
  );

  const closeOnMobileNav = useCallback(() => {
    if (isMobile) setSidebar(false);
  }, [isMobile, setSidebar]);

  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  const toggleSection = useCallback((key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const isExpanded = useCallback(
    (key) => expandedKeys.has(key),
    [expandedKeys]
  );

  const isMatch = useCallback(
    (matches) => {
      if (!matches || matches.length === 0) return false;
      return matches.some((m) => {
        if (typeof m === "string") return currentRoute === m;
        if (m && typeof m.startsWith === "string") {
          return currentRoute.startsWith(m.startsWith);
        }
        return false;
      });
    },
    [currentRoute]
  );

  const [logoutModalShow, setLogoutModalShow] = useState(false);
  const openLogoutModal = useCallback(() => setLogoutModalShow(true), []);
  const closeLogoutModal = useCallback(() => setLogoutModalShow(false), []);

  const userLogout = useCallback(() => {
    PostCallWithErrorResponse(ApiConfig.LOGOUT).then((res) => {
      if (res?.json?.Success === true) {
        ToastMsg("success", res.json.Message);
      } else {
        ToastMsg("error", res?.json?.Message);
      }
    });
  }, []);

  const confirmLogout = useCallback(() => {
    userLogout();
    localStorage.clear();
    if (socket) socket.disconnect();
    setSocket(null);
    setUserData({});
    setLoggedIn(false);
    setLogoutModalShow(false);
    setOpenSessionLogoutModal(false);
  }, [
    userLogout,
    socket,
    setSocket,
    setUserData,
    setLoggedIn,
    setOpenSessionLogoutModal,
  ]);

  const getProjects = useCallback(() => {
    if (!socket) return;
    socket.emit("get-projects-android", {});
    socket.on("get-projects-android", (data) => {
      setProjects(data.map((item) => ({ ...item, colapse: false })));
    });
  }, [socket, setProjects]);

  const getTeams = useCallback(() => {
    if (!socket) return;
    socket.emit("get-teams-android", {});
    socket.on("get-teams-android", (data) => {
      setTeams(data.map((item) => ({ ...item, colapse: false })));
    });
  }, [socket, setTeams]);

  const getCallHistory = useCallback(() => {
    if (!socket) return;
    setCallPage(0);
    setCallLoadMore(true);
    socket.emit("call-history", { page: 0 });
  }, [socket, setCallPage, setCallLoadMore]);

  const getPrivateChat = useCallback(() => {
    if (!socket) return;
    socket.emit("get-private-chat-list", {});
  }, [socket]);

  const workFlowTask = useCallback(() => {
    simpleGetCallWithErrorResponse(ApiConfig.WORKFLOW_TASK_FORM).then((res) => {
      if (!res?.json?.Data?.WorkflowId) {
        ToastMsg("error", "You don't have Task");
      } else {
        navigate(
          `/dynamicComplteTask/${res?.json?.Data?.TaskAllocationId}/${res?.json?.Data?.TaskId}`
        );
      }
    });
  }, [navigate]);

  const decryptWorkflowToken = useCallback(() => {
    const data = localStorage.getItem(ENCRYPTED_LS_KEY);
    if (!data) return null;
    try {
      return CryptoJS.AES.decrypt(data, ENCRYPTED_SECRET).toString(
        CryptoJS.enc.Utf8
      );
    } catch {
      return null;
    }
  }, []);

  const onSubItemNavigate = useCallback(
    ({ socketTrigger } = {}) => {
      closeOnMobileNav();
      if (socketTrigger && SOCKET_CLEAR_CHAT.has(socketTrigger)) {
        setActiveChat({});
        setCurrentSChannel(null);
      }
      if (socketTrigger === "getProjects") getProjects();
      else if (socketTrigger === "getTeams") getTeams();
      else if (socketTrigger === "getCallHistory") getCallHistory();
      else if (socketTrigger === "getPrivateChat") getPrivateChat();
    },
    [
      closeOnMobileNav,
      getCallHistory,
      getPrivateChat,
      getProjects,
      getTeams,
      setActiveChat,
      setCurrentSChannel,
    ]
  );

  const counters = useMemo(
    () => ({
      unreadCount,
      chatCount,
      unreadProjectCount,
      unreadTeamCount,
      pendingWorkflowTaskCount,
    }),
    [
      unreadCount,
      chatCount,
      unreadProjectCount,
      unreadTeamCount,
      pendingWorkflowTaskCount,
    ]
  );

  const handlers = useMemo(
    () => ({
      workFlowTask,
      decryptWorkflowToken,
    }),
    [workFlowTask, decryptWorkflowToken]
  );

  return {
    // chrome
    collapsed: !sidebar,
    toggleCollapse,
    closeOnMobileNav,
    isMobile,
    dark: Dark,

    // user / permissions
    userData,
    permissions,
    can,
    canAny,
    isEmployeeWalletOnly,
    isWalletAdmin,

    // route
    currentRoute,
    isMatch,

    // accordion expansion
    isExpanded,
    toggleSection,

    // counters
    counters,

    // navigation
    handleNavigate,
    onSubItemNavigate,
    handlers,

    // modals
    logoutModalShow,
    openLogoutModal,
    closeLogoutModal,
    sessionExpiredModalShow: openSessionLogoutModal,
    confirmLogout,
  };
};

export default useSidebar;
